// Shared pieces for the AI employee team (每日看板 → 我的團隊) and each employee's page
// (/owner-board/team/:key). Data: GET /api/agent-threads/team and /team/:key, built from the
// snapshot the PC runner pushes every 10 minutes (workspace scripts/agents/employee.py snapshot).

import React from 'react';
import { Box } from '@mui/material';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import api from '../../services/api';
import { ApprovalMeta, WORKSTREAM_COLOR } from './agentShared';

export interface Kpi {
  label: string;
  detail?: string;
  value: number | null;
  at: string;
}
export interface ScoreKpi {
  id: string;
  label: string;
  unit: string;
  better: 'up' | 'down';
  target: number | null;
  draft: boolean;
  how?: string;
  value: number | null;
  prev: number | null;
  state: 'hit' | 'miss' | 'none';
  at: string | null;
  detail: string | null;
  series: { d: string; v: number }[];
}
export type IdeaStatus = 'proposed' | 'doing' | 'testing' | 'shipped' | 'won' | 'lost' | 'dropped';
export interface Idea {
  id: number;
  kpi: string;
  title: string;
  why?: string;
  expect?: string;
  effort?: '小' | '中' | '大';
  status: IdeaStatus;
  note?: string | null;
  check_on?: string | null;
  result?: string | null;
  at: string;
  updated_at?: string | null;
}
export interface ActionClass {
  cls: string;
  label: string;
  stage: 'intern' | 'autonomous' | 'floor';
  record: string;
  need: number | null;
}
export interface Snapshot {
  role: string;
  hour: number;
  team: string | null;
  kpis: Kpi[];
  kpi_day: string | null;
  scoreboard?: ScoreKpi[];
  ideas?: Idea[];
  actions_today: string[];
  findings_today: string[];
  predictions_open: number;
  in_progress: string[];
  classes: ActionClass[];
  ready: string[];
  pushed_at?: string;
}
export interface TeamThread {
  id: number;
  status: 'idle' | 'queued' | 'running' | 'awaiting_approval' | 'failed';
  status_line: string | null;
  created_at: string;
  updated_at: string;
}
export interface TeamApproval {
  id: number;
  thread_id: number;
  content: string;
  meta: ApprovalMeta;
  created_at: string;
}
export type TaskState = 'queued' | 'running' | 'approval' | 'failed' | 'done' | 'unproven' | 'needs_you' | 'blocked';
/** One task Magnus assigned (its own thread, preset task_<key>). */
export interface Task {
  id: number;
  title: string;
  ask: string;
  status: TeamThread['status'];
  status_line: string | null;
  state: TaskState;
  evidence: string | null;
  report?: { content: string; created_at: string } | null;
  archived: boolean;
  created_at: string;
  updated_at: string;
}
export const TASK_STATE: Record<TaskState, { label: string; bg: string; fg: string }> = {
  queued: { label: '排隊中', bg: '#E6F1FB', fg: '#0C447C' },
  running: { label: '進行中', bg: '#E6F1FB', fg: '#0C447C' },
  approval: { label: '等你批准', bg: '#FAEEDA', fg: '#854F0B' },
  needs_you: { label: '等你回覆', bg: '#FAEEDA', fg: '#854F0B' },
  unproven: { label: '說完成·沒附證據', bg: '#FAEEDA', fg: '#854F0B' },
  blocked: { label: '做不到', bg: '#FCEBEB', fg: '#A32D2D' },
  failed: { label: '沒跑完', bg: '#FCEBEB', fg: '#A32D2D' },
  done: { label: '完成', bg: '#EAF3DE', fg: '#3B6D11' },
};
/** Tasks that need Magnus to look (answer, check, unblock). Approvals are counted separately. */
export const taskNeedsYou = (t: Pick<Task, 'state'>) => ['needs_you', 'unproven', 'blocked', 'failed'].includes(t.state);

export interface Employee {
  key: string;
  name: string;
  workstream: string;
  snapshot: Snapshot | null;
  thread: TeamThread | null;
  report: { content: string; created_at: string } | null;
  approvals: TeamApproval[];
  tasks?: Task[];
}

export const IDEA_STATUS: Record<IdeaStatus, string> = {
  proposed: '提案',
  doing: '在做',
  testing: '測試中',
  shipped: '已上線',
  won: '有效',
  lost: '沒效',
  dropped: '不做',
};
export const IDEA_OPEN: IdeaStatus[] = ['proposed', 'doing', 'testing', 'shipped'];

export const taipeiDay = (iso?: string | null) =>
  iso ? new Date(new Date(iso).getTime() + 8 * 3600 * 1000).toISOString().slice(0, 10) : null;
const taipeiHour = () => new Date(Date.now() + 8 * 3600 * 1000).getUTCHours();

export const fmt = (v: number | null | undefined) =>
  v == null ? '—' : Math.abs(v) >= 1000 ? Math.round(v).toLocaleString() : String(Math.round(v * 100) / 100);

/** A value with its unit, e.g. 1.8%, 7.57x, NT$7,669, 3.49 天. */
export const withUnit = (v: number | null | undefined, unit: string) => {
  if (v == null) return '—';
  if (unit === '%' || unit === 'x' || unit === '‰') return `${fmt(v)}${unit}`;
  if (unit === 'NT$') return `NT$${fmt(v)}`;
  return `${fmt(v)} ${unit}`;
};

/** Is this change good for the KPI? +1 good, -1 bad, 0 flat/unknown. */
export const deltaTone = (k: Pick<ScoreKpi, 'better' | 'value' | 'prev'>) => {
  if (k.value == null || k.prev == null || k.value === k.prev) return 0;
  const up = k.value > k.prev;
  return (k.better === 'down' ? !up : up) ? 1 : -1;
};

export const GOOD = '#3B6D11';
export const BAD = '#A32D2D';
export const AMBER = '#854F0B';

export type Status = { label: string; tone: 'work' | 'wait' | 'done' | 'off' | 'fail'; spin?: boolean };

export const statusOf = (e: Pick<Employee, 'thread' | 'approvals' | 'snapshot' | 'tasks'>): Status => {
  const today = taipeiDay(new Date().toISOString());
  const t = e.thread;
  const ranToday = t && taipeiDay(t.created_at) === today;
  const tasks = (e.tasks || []).filter((x) => !x.archived);
  const taskWait = tasks.filter(taskNeedsYou).length;
  if (e.approvals.length + taskWait) return { label: `等你 ${e.approvals.length + taskWait}`, tone: 'wait' };
  if (ranToday && (t!.status === 'running' || t!.status === 'queued'))
    return { label: t!.status === 'queued' ? '準備上班' : '上班中', tone: 'work', spin: true };
  if (tasks.some((x) => x.state === 'running' || x.state === 'queued'))
    return { label: '做任務中', tone: 'work', spin: true };
  if (ranToday && t!.status === 'failed') return { label: '沒跑完', tone: 'fail' };
  if (ranToday) return { label: '下班', tone: 'done' };
  const hour = e.snapshot?.hour;
  if (hour != null && taipeiHour() < hour) return { label: `${hour}:00 上班`, tone: 'off' };
  return { label: '今天還沒上班', tone: 'off' };
};

export const TONE: Record<Status['tone'], { bg: string; fg: string }> = {
  work: { bg: '#E6F1FB', fg: '#0C447C' },
  wait: { bg: '#FAEEDA', fg: AMBER },
  done: { bg: '#EAF3DE', fg: GOOD },
  off: { bg: '#F1EFE8', fg: '#5F5E5A' },
  fail: { bg: '#FCEBEB', fg: BAD },
};

export const autonomyOf = (s: Snapshot | null) => {
  const classes = s?.classes || [];
  const open = classes.filter((c) => c.stage !== 'floor');
  return { auto: open.filter((c) => c.stage === 'autonomous').length, total: open.length, ready: s?.ready?.length || 0 };
};

export const scoreOf = (s: Snapshot | null) => {
  const board = (s?.scoreboard || []).filter((k) => k.target != null && k.value != null);
  return { hit: board.filter((k) => k.state === 'hit').length, total: board.length };
};

export const openIdeas = (s: Snapshot | null) => (s?.ideas || []).filter((i) => IDEA_OPEN.includes(i.status));

export const Avatar: React.FC<{ name: string; workstream: string; size?: number }> = ({ name, workstream, size = 40 }) => {
  const c = WORKSTREAM_COLOR[workstream] || WORKSTREAM_COLOR['營運'];
  return (
    <Box
      sx={{ width: size, height: size, borderRadius: '50%', bgcolor: c.bg, color: c.fg, flexShrink: 0, display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: size * 0.4 }}
    >
      {name.slice(0, 1)}
    </Box>
  );
};

export const StatusPill: React.FC<{ st: Status }> = ({ st }) => (
  <Box
    component="span"
    sx={{ bgcolor: TONE[st.tone].bg, color: TONE[st.tone].fg, fontSize: 11, fontWeight: 700, px: 1, py: 0.25,
      borderRadius: 99, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
  >
    {st.spin && <AutorenewRoundedIcon sx={{ fontSize: 13 }} />}
    {st.tone === 'done' && <CheckRoundedIcon sx={{ fontSize: 13 }} />}
    {st.label}
  </Box>
);

/** Tiny trend line; the dashed line is the target when it falls inside the drawn range. */
export const Sparkline: React.FC<{ k: ScoreKpi; height?: number }> = ({ k, height = 30 }) => {
  const pts = k.series || [];
  if (pts.length < 2) return null;
  const vals = pts.map((p) => p.v);
  let lo = Math.min(...vals);
  let hi = Math.max(...vals);
  if (k.target != null && k.target >= lo - (hi - lo) && k.target <= hi + (hi - lo)) {
    lo = Math.min(lo, k.target);
    hi = Math.max(hi, k.target);
  }
  const span = hi - lo || 1;
  const W = 100;
  const y = (v: number) => height - 3 - ((v - lo) / span) * (height - 6);
  const x = (i: number) => (i / (pts.length - 1)) * W;
  const line = pts.map((p, i) => `${x(i).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const color = k.state === 'hit' ? GOOD : k.state === 'miss' ? AMBER : '#888';
  return (
    <Box component="svg" viewBox={`0 0 ${W} ${height}`} preserveAspectRatio="none"
      sx={{ width: '100%', height, display: 'block', overflow: 'visible' }} aria-hidden>
      {k.target != null && k.target >= lo && k.target <= hi && (
        <line x1="0" x2={W} y1={y(k.target)} y2={y(k.target)} stroke="#bbb" strokeDasharray="3 3"
          strokeWidth="1" vectorEffect="non-scaling-stroke" />
      )}
      <polyline points={line} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke"
        strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(pts.length - 1)} cy={y(pts[pts.length - 1].v)} r="2.5" fill={color} />
    </Box>
  );
};

/** 目標 ≥ 2% · 差 0.2% / 達標 */
export const targetText = (k: ScoreKpi) => {
  if (k.target == null) return '還沒定目標';
  const t = `目標 ${k.better === 'down' ? '≤' : '≥'} ${withUnit(k.target, k.unit)}${k.draft ? '（草定）' : ''}`;
  if (k.value == null) return t;
  if (k.state === 'hit') return `${t} · 達標`;
  return `${t} · 差 ${withUnit(Math.abs(k.target - k.value), k.unit)}`;
};

/** Assign one task: its own thread, queued at once. */
export const assignTask = (key: string, text: string) => api.post(`/api/agent-threads/team/${key}/tasks`, { text });

/** Send Magnus's words to the employee: into today's thread, or start one with them. */
export const sendToEmployee = async (key: string, thread: { id: number } | null | undefined, text: string) => {
  if (thread) return api.post(`/api/agent-threads/${thread.id}/messages`, { text });
  return api.post('/api/agent-threads', { preset_key: `emp_${key}`, text });
};
